import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '@thd-nucleus/app-render';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  Route, Routes, ScrollToTop, useLocation
} from '@thd-olt-component-react/router';
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { NinetailedPreviewPlugin } from '@ninetailed/experience.js-plugin-preview';
import { NinetailedInsightsPlugin } from '@ninetailed/experience.js-plugin-insights';
import { NinetailedProvider } from '@ninetailed/experience.js-react';
import { fetchNinetailedComponentExperiencesFormatted } from './contentful/utils/ninetailed';
import { Error } from './pages';
import { getRoutes } from './routes';
// fetch from utilities
import './styles/app.scss';

export const App = () => {
  const { instance } = useContext(AppContext);
  const { search: querystring, pathname } = useLocation();

  const {
    currentRouteName,
    currentRouteComponent
  } = instance.router;
  const routes = getRoutes(currentRouteName, currentRouteComponent);

  const [formattedExperiences, setFormattedExperiences] = useState(null); // null means "not loaded"
  const pathWithQueryParams = `${pathname}${querystring}`;

  const NINETAILED_CLIENT_ID = useConfigService('contentful.ninetailed.client.id') || '';
  const CONTENTFUL_ENV = useConfigService('contentful.env') || 'dev';
  const CONTENTFUL_SPACE_ID = useConfigService('contentful.space.id.martech') || '';
  const CONTENTFUL_PREVIEW_TOKEN = useConfigService('contentful.tokens.martech.preview') || '';

  // Ninetailed environment mapping
  const NINETAILED_ENV = CONTENTFUL_ENV === 'master' ? 'main' : 'development';

  const isPreviewLink = !!instance?.headers?.['x-content-preview']
    || pathWithQueryParams.indexOf('render*') > -1
    || pathWithQueryParams.indexOf('contentPV') > -1
    || pathWithQueryParams.indexOf('bassPV') > -1;

  const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);

  useEffect(() => {
    if (isPreviewLink) {
      fetchNinetailedComponentExperiencesFormatted(
        lastSegment,
        CONTENTFUL_PREVIEW_TOKEN,
        CONTENTFUL_SPACE_ID,
        CONTENTFUL_ENV
      )
        .then(setFormattedExperiences)
        .catch(() => {
          setFormattedExperiences([]); // fallback to empty array
        });
    } else {
      setFormattedExperiences([]); // fallback if no slug or not in preview mode
    }
  }, [isPreviewLink, lastSegment, CONTENTFUL_PREVIEW_TOKEN, CONTENTFUL_SPACE_ID,
    CONTENTFUL_ENV]);

  if (isPreviewLink && formattedExperiences === null) {
    return null; // or return a loader/spinner if you want
  }

  const plugins = isPreviewLink && formattedExperiences
    ? [
      new NinetailedPreviewPlugin({
        experiences: formattedExperiences,
        audiences: formattedExperiences.map((exp) => exp.audience).filter(Boolean),
        onOpenExperienceEditor: (experience) => {},
        onOpenAudienceEditor: (audience) => {},
        ui: { opener: { hide: false } }
      }), new NinetailedInsightsPlugin()
    ] : [new NinetailedInsightsPlugin()];

  return (
    <NinetailedProvider
      clientId={NINETAILED_CLIENT_ID}
      environment={NINETAILED_ENV}
      plugins={plugins}
    >
      <ContentfulLivePreviewProvider locale="en-US" targetOrigin="https://app.contentful.com">
        <ScrollToTop />
        <Routes>
          <Route exact path={routes.landingA.path} element={<routes.landingA.component />} />
          <Route exact path={routes.landingB.path} element={<routes.landingB.component />} />
          <Route exact path={routes.landingC.path} element={<routes.landingC.component />} />
          <Route exact path={routes.landingTest.path} element={<routes.landingTest.component />} />
          {/* <Route exact path={routes.home.path} element={<routes.home.component />} /> */}
          <Route path="/error" element={<Error />} />
          <Route element={<Error />} />
        </Routes>
      </ContentfulLivePreviewProvider>
    </NinetailedProvider>
  );
};

App.propTypes = {};

App.defaultProps = {};